/**
 * Pure handlers for panel-mutation workspace actions published by AI client
 * tools (see `clientTools/visualize/`).
 *
 * Contract shared by every handler:
 * - Validation is atomic: either every requested message path is applied, or
 *   nothing is dispatched and the result carries per-path reasons.
 * - Handlers never throw. The PubSubBus treats a throwing subscriber as a
 *   failed publish and the error detail would be lost, so failures are
 *   reported through the returned {@link WorkspaceActionResult} instead.
 */
import {
  constructTree,
  MessagePathNode,
  TopicRecord,
} from "@/shared/domain/topics";
import {
  type MapPair,
  SignalPanelType,
  validateSignalForPanelType,
  validateSignalsForNewPanel,
} from "@/shared/domain/workspaces/signalValidation";
import {
  AddToPanelActionPayload,
  CreatePanelActionPayload,
  PanelPlacementInput,
  RemoveFromPanelActionPayload,
  RemovePanelActionPayload,
  SetLogPanelFiltersActionPayload,
  WorkspaceActionEntry,
  WorkspaceActionResult,
} from "@/shared/services/AiAgentThreadPubSubBus/models";
import {
  type Dispatch,
  type PanelState,
  type State,
  type TopicData,
  actions,
  isImagePanelState,
  isLogPanelState,
  isMapPanelState,
  type MapPanelState,
  isPlotPanelState,
  PanelType,
  Placement,
} from "@/shared/state/visualization";
import { LayoutOrientation } from "@/shared/state/visualization/actions";
import {
  selectLayoutById,
  selectPanelById,
} from "@/shared/state/visualization/hooks";
import {
  ALL_COLUMNS_ID,
  LOG_TIME_ID,
  columnIdForParts,
  isFiniteNumericString,
  logTimeValueProblem,
  type FilterCondition,
  type FilterStage,
} from "@/shared/state/visualization/schema/v1/log-panel";

import { canAddToPanel } from "../DndContainer/dropHandlers";
import { columnFiltersEqual } from "../Panel/LogPanel/columnFilters/commit";
import {
  conditionMeta,
  getConditionsForDataType,
  isValuelessCondition,
  needsSecondValue,
} from "../Panel/LogPanel/columnFilters/conditionMeta";
import { deriveLiveColumnFilters } from "../Panel/LogPanel/columnFilters/derive";
import { isValidRegex } from "../Panel/LogPanel/columnFilters/regexUtils";
import {
  type ColumnFilter,
  hasAllColumnsFilter,
  hasPerColumnFilters,
} from "../Panel/LogPanel/columnFilters/state";
import { getDynamicColumns } from "../Panel/LogPanel/getDynamicColumns";
import {
  A_LOT_OF_LOGS,
  estimateTopicMessageCount,
} from "../Panel/LogPanel/logRowCounts";
import { resolveSearchVisible } from "../Panel/LogPanel/resolveSearchVisible";
import { panelAcceptsMessagePath } from "../Panel/predicates";

interface InvalidPath {
  messagePathId: string;
  reason: string;
}

function failure(
  error: string,
  invalidPaths?: InvalidPath[],
): WorkspaceActionResult {
  return {
    ok: false,
    error,
    ...(invalidPaths && invalidPaths.length > 0 ? { invalidPaths } : {}),
  };
}

interface InvalidTarget {
  target: string;
  reason: string;
}

/**
 * Failure for an action whose targets are not all message paths, so their
 * reasons cannot travel in a `message_path_id`-named field without misnaming
 * what they are. See `WorkspaceActionResult.invalidTargets`.
 */
function targetFailure(
  error: string,
  invalidTargets: InvalidTarget[],
): WorkspaceActionResult {
  return {
    ok: false,
    error,
    ...(invalidTargets.length > 0 ? { invalidTargets } : {}),
  };
}

function panelNotFound(panelId: string): WorkspaceActionResult {
  return failure(
    `Panel "${panelId}" was not found in the current workspace. ` +
      "It may have been closed since the visualizer state was captured.",
  );
}

const ORIENTATION_MAP: Record<
  PanelPlacementInput["orientation"],
  LayoutOrientation
> = {
  top: LayoutOrientation.TOP,
  right: LayoutOrientation.RIGHT,
  bottom: LayoutOrientation.BOTTOM,
  left: LayoutOrientation.LEFT,
};

interface ResolvedEntry {
  messagePathId: string;
  node: MessagePathNode;
  topicData: TopicData;
}

/**
 * Resolve workspace action entries to message path nodes with full topic
 * trees behind them.
 *
 * Full trees (via {@link constructTree}) matter: validation like
 * `canAddToLogPanel`'s shared-column check walks the node's whole topic tree,
 * so a minimal single-branch tree would produce false rejections.
 */
function resolveEntries(entries: WorkspaceActionEntry[]): {
  resolved: ResolvedEntry[];
  invalid: InvalidPath[];
} {
  const resolved: ResolvedEntry[] = [];
  const invalid: InvalidPath[] = [];

  const topicsById = new Map<string, TopicRecord>();
  entries.forEach((entry) => {
    if (!topicsById.has(entry.topic.topic_id)) {
      topicsById.set(entry.topic.topic_id, entry.topic);
    }
  });
  const nodesByMessagePathId = new Map<string, MessagePathNode>();
  for (const tree of constructTree([...topicsById.values()])) {
    for (const node of tree) {
      nodesByMessagePathId.set(node.id, node);
    }
  }

  const seen = new Set<string>();
  for (const entry of entries) {
    const messagePathId = entry.messagePath.message_path_id;

    if (seen.has(messagePathId)) {
      invalid.push({
        messagePathId,
        reason:
          "Duplicated in the request; each message path may appear only once.",
      });
      continue;
    }
    seen.add(messagePathId);

    const node = nodesByMessagePathId.get(messagePathId);
    if (!node) {
      invalid.push({
        messagePathId,
        reason: `Message path was not found in topic "${entry.topic.topic_name}".`,
      });
      continue;
    }

    try {
      resolved.push({ messagePathId, node, topicData: node.toTopicData() });
    } catch (err) {
      invalid.push({
        messagePathId,
        reason: `Message path "${node.data.message_path}" cannot be visualized: ${
          err instanceof Error ? err.message : String(err)
        }`,
      });
    }
  }

  return { resolved, invalid };
}

/**
 * Node-level create validation that the shared record-level pass
 * ({@link validateSignalsForNewPanel}) cannot express: incremental
 * `canAddToPanel` simulation for log/raw-message panels, which needs hydrated
 * topic trees (e.g. log column-conflict checks).
 */
function validateCreateBatch(
  panelType: PanelType,
  entries: ResolvedEntry[],
): InvalidPath[] {
  const invalid: InvalidPath[] = [];

  if (panelType === PanelType.Log || panelType === PanelType.RawMessage) {
    // Simulate incremental adds against the panel formed by the predecessors,
    // reusing the same validation the drag-and-drop path applies.
    const accumulated: TopicData[] = [];
    for (const entry of entries) {
      if (accumulated.length > 0) {
        const simulatedPanel = {
          id: "pending-panel",
          title: "",
          type: panelType,
          data: accumulated,
        } as PanelState;
        const result = canAddToPanel(simulatedPanel, entry.node);
        if (!result.isValid) {
          invalid.push({
            messagePathId: entry.messagePathId,
            reason: result.errors.join(" "),
          });
          continue;
        }
      }
      accumulated.push(entry.topicData);
    }
  }

  return invalid;
}

/**
 * The `TopicData` pairs one `addPathToMapPanel` dispatch each would draw, or
 * undefined when a pair member did not resolve.
 *
 * Latitude first: `getGeoMessagePaths` reads a map path's first member as the
 * latitude, and a plain `number` coordinate says nothing about which half it
 * is. Both map dispatch sites go through here so that order is written once.
 *
 * All-or-nothing on purpose. A missing member is unreachable — resolution
 * failing would have returned before this — but returning the whole set or
 * none of it means no caller can dispatch half a fleet and leave a part-built
 * panel behind with no error to explain it.
 */
function mapPathData(
  mapPairs: MapPair[],
  resolved: ResolvedEntry[],
): TopicData[][] | undefined {
  const topicDataById = new Map(
    resolved.map((entry) => [entry.messagePathId, entry.topicData]),
  );
  const pathData: TopicData[][] = [];
  for (const pair of mapPairs) {
    const latitude = topicDataById.get(
      pair.latitude.messagePath.message_path_id,
    );
    const longitude = topicDataById.get(
      pair.longitude.messagePath.message_path_id,
    );
    if (!latitude || !longitude) {
      return undefined;
    }
    pathData.push([latitude, longitude]);
  }
  return pathData;
}

export function handleCreatePanel(
  state: State,
  dispatch: Dispatch,
  payload: CreatePanelActionPayload,
): WorkspaceActionResult {
  // Record-level rules (type consensus, duplicates, acceptance, same-topic,
  // map pairing incl. sibling bundling) are shared with the deep-link path.
  const signalsValidation = validateSignalsForNewPanel(
    payload.entries,
    payload.panelType,
  );
  if (!signalsValidation.ok) {
    return failure(signalsValidation.error, signalsValidation.invalid);
  }
  const { panelType } = signalsValidation;

  const { resolved, invalid } = resolveEntries(signalsValidation.entries);
  if (invalid.length > 0) {
    return failure("Some message paths cannot be opened.", invalid);
  }

  const batchInvalid = validateCreateBatch(panelType, resolved);
  if (batchInvalid.length > 0) {
    return failure(
      `Some message paths cannot be combined into one ${panelType} panel.`,
      batchInvalid,
    );
  }

  let placement: Placement | undefined = undefined;
  if (payload.placement) {
    const siblingLayout = selectLayoutById(
      state,
      payload.placement.relativeToPanelId,
    );
    if (!siblingLayout) {
      return panelNotFound(payload.placement.relativeToPanelId);
    }
    placement = {
      siblingLayout,
      orientation: ORIENTATION_MAP[payload.placement.orientation],
    };
  }

  const panelId = crypto.randomUUID();

  if (panelType === PanelType.Map && signalsValidation.mapPairs) {
    // A create payload's whole data array becomes ONE map path in the
    // reducer, so a panel opened with several tracks would render only the
    // first. Open it empty and add a path per validated pair instead — the
    // same dispatch the settings form and drag-and-drop use.
    const pathData = mapPathData(signalsValidation.mapPairs, resolved);
    if (!pathData) {
      return failure("Some message paths cannot be opened.");
    }

    dispatch(actions.createPanel(null, panelType, placement, panelId));
    pathData.forEach((data) => {
      dispatch(actions.addPathToMapPanel(panelId, data));
    });

    return { ok: true, panelId, panelType };
  }

  dispatch(
    actions.createPanel(
      resolved.map((entry) => entry.topicData),
      panelType,
      placement,
      panelId,
    ),
  );

  return { ok: true, panelId, panelType };
}

/**
 * Add latitude/longitude pairs to an existing map panel.
 *
 * Maps take their own route because a map path is a pair, not a signal: the
 * generic add loop asks `panelAcceptsMessagePath` whether each path can join
 * the panel on its own, which no half of a coordinate pair can answer, and it
 * would bundle a partner by data type alone. Pairing runs through
 * {@link validateSignalsForNewPanel} instead, the same call panel creation
 * makes, so a pair the agent can open is exactly a pair it can add.
 */
function addPairsToMapPanel(
  dispatch: Dispatch,
  panel: MapPanelState,
  payload: AddToPanelActionPayload,
): WorkspaceActionResult {
  const pairing = validateSignalsForNewPanel(payload.entries, PanelType.Map);
  if (!pairing.ok) {
    return failure(pairing.error, pairing.invalid);
  }

  const { resolved, invalid } = resolveEntries(pairing.entries);
  if (invalid.length > 0) {
    return failure(
      `Some message paths cannot be added to panel "${payload.panelId}".`,
      invalid,
    );
  }

  // A repeat add would be dropped silently by the reducer's id check, so it
  // is reported here instead. Either member already on the panel is enough:
  // the pair it belongs to is the track the caller is asking for again.
  const shownOnPanel = new Set(
    panel.data.flatMap((path) => path.data.map((item) => item.messagePath.id)),
  );
  const duplicates: InvalidPath[] = pairing.entries
    .filter((entry) => shownOnPanel.has(entry.messagePath.message_path_id))
    .map((entry) => ({
      messagePathId: entry.messagePath.message_path_id,
      reason: `Message path "${entry.messagePath.message_path}" is already in the panel.`,
    }));
  if (duplicates.length > 0) {
    return failure(
      `Some message paths cannot be added to panel "${payload.panelId}".`,
      duplicates,
    );
  }

  const pathData = mapPathData(pairing.mapPairs ?? [], resolved);
  if (!pathData) {
    return failure(
      `Some message paths cannot be added to panel "${payload.panelId}".`,
    );
  }

  pathData.forEach((data) => {
    dispatch(actions.addPathToMapPanel(panel.id, data));
  });

  return { ok: true, panelId: payload.panelId, panelType: PanelType.Map };
}

export function handleAddToPanel(
  state: State,
  dispatch: Dispatch,
  payload: AddToPanelActionPayload,
): WorkspaceActionResult {
  const panel = selectPanelById(state, payload.panelId);
  if (!panel) {
    return panelNotFound(payload.panelId);
  }

  if (payload.entries.length === 0) {
    return failure("At least one message path is required.");
  }

  if (isMapPanelState(panel)) {
    return addPairsToMapPanel(dispatch, panel, payload);
  }

  // Run the same record-level checks panel creation runs (leaf-only plots,
  // MCAP-backed image clips, readable representations) BEFORE node
  // resolution, so create and add cannot drift apart on what a panel type
  // accepts and the specific rejection reason wins over a generic
  // resolution error.
  const signalPanelTypes: PanelType[] = [
    PanelType.Plot,
    PanelType.Log,
    PanelType.RawMessage,
    PanelType.Image,
    PanelType.Map,
  ];
  if (signalPanelTypes.includes(panel.type)) {
    const acceptanceInvalid: InvalidPath[] = [];
    payload.entries.forEach((record) => {
      const reason = validateSignalForPanelType(
        panel.type as SignalPanelType,
        record,
      );
      if (reason) {
        acceptanceInvalid.push({
          messagePathId: record.messagePath.message_path_id,
          reason,
        });
      }
    });
    if (acceptanceInvalid.length > 0) {
      return failure(
        `Some message paths cannot be added to panel "${payload.panelId}".`,
        acceptanceInvalid,
      );
    }
  }

  const { resolved, invalid } = resolveEntries(payload.entries);

  // Validate every path against the panel as it would look once its
  // predecessors in this batch were added, so intra-batch conflicts
  // (duplicates, log column clashes) are caught before any dispatch.
  // Widened back to PanelState: the map early-return above narrows `panel`,
  // and the simulation returns whatever panel type it was handed.
  let simulatedPanel: PanelState = panel;
  for (const entry of resolved) {
    if (!panelAcceptsMessagePath(simulatedPanel, entry.node)) {
      invalid.push({
        messagePathId: entry.messagePathId,
        reason:
          `Data type "${entry.node.data.canonical_data_type}" is not supported ` +
          `by this ${panel.type} panel.`,
      });
      continue;
    }

    const addCheck = canAddToPanel(simulatedPanel, entry.node);
    if (!addCheck.isValid) {
      invalid.push({
        messagePathId: entry.messagePathId,
        reason: addCheck.errors.join(" "),
      });
      continue;
    }

    const duplicateCheck = validateNotAlreadyInPanel(simulatedPanel, entry);
    if (duplicateCheck) {
      invalid.push(duplicateCheck);
      continue;
    }

    simulatedPanel = simulatePanelWithEntry(simulatedPanel, entry);
  }

  if (invalid.length > 0) {
    return failure(
      `Some message paths cannot be added to panel "${payload.panelId}".`,
      invalid,
    );
  }

  // Stage every dispatch before executing any, so no failure mode can leave
  // the batch partially applied — validation above is the only gate.
  const staged: (() => void)[] = [];
  for (const entry of resolved) {
    switch (panel.type) {
      case PanelType.Plot:
        staged.push(() =>
          dispatch(actions.addSeriesToPlotPanel(panel.id, entry.topicData)),
        );
        break;
      case PanelType.Log:
        staged.push(() =>
          dispatch(actions.addDataToLogPanel(panel.id, entry.topicData)),
        );
        break;
      case PanelType.RawMessage:
        staged.push(() =>
          dispatch(actions.addDataToRawMessagePanel(panel.id, entry.topicData)),
        );
        break;
      case PanelType.Image:
        staged.push(() =>
          dispatch(actions.putImagePanelData(panel.id, entry.topicData)),
        );
        break;
      default:
        // Unreachable: map panels returned via `addPairsToMapPanel` above,
        // and panelAcceptsMessagePath rejects every other type during
        // validation.
        break;
    }
  }

  staged.forEach((run) => run());
  return { ok: true, panelId: payload.panelId, panelType: panel.type };
}

/**
 * Duplicate checks for panel types whose add path reports duplicates through
 * dispatch-time `onError` callbacks instead of `canAddToPanel`.
 * Returns an {@link InvalidPath} when `entry` is already on the panel.
 *
 * Image only: map panels never reach this loop — {@link addPairsToMapPanel}
 * takes them, and owns the map duplicate rule outright so there is only one
 * copy of it to keep correct.
 */
function validateNotAlreadyInPanel(
  panel: PanelState,
  entry: ResolvedEntry,
): InvalidPath | undefined {
  const alreadyInPanel = (): boolean => {
    if (isImagePanelState(panel)) {
      return panel.data.some(
        (clip) => clip.data.messagePath.id === entry.messagePathId,
      );
    }
    return false;
  };

  if (alreadyInPanel()) {
    return {
      messagePathId: entry.messagePathId,
      reason: `Message path "${entry.node.data.message_path}" is already in the panel.`,
    };
  }
  return undefined;
}

/**
 * Return a copy of `panel` as it would look after `entry` was added, so
 * validation sees each entry's batch predecessors alongside the live panel
 * data. Only the fields the validation predicates read are simulated.
 */
function simulatePanelWithEntry(
  panel: PanelState,
  entry: ResolvedEntry,
): PanelState {
  if (isPlotPanelState(panel)) {
    return {
      ...panel,
      data: [
        ...panel.data,
        {
          data: entry.topicData,
          id: `simulated-${entry.messagePathId}`,
          visible: true,
        },
      ],
    };
  }
  if (isLogPanelState(panel) || panel.type === PanelType.RawMessage) {
    return {
      ...panel,
      data: [...panel.data, entry.topicData],
    } as PanelState;
  }
  if (isImagePanelState(panel)) {
    return {
      ...panel,
      data: [
        ...panel.data,
        { data: entry.topicData, id: `simulated-${entry.messagePathId}` },
      ],
    };
  }
  if (isMapPanelState(panel)) {
    return {
      ...panel,
      data: [
        ...panel.data,
        {
          id: `simulated-${entry.messagePathId}`,
          data: [{ ...entry.topicData, config: {} }],
        },
      ],
    } as PanelState;
  }
  return panel;
}

export function handleRemoveFromPanel(
  state: State,
  dispatch: Dispatch,
  payload: RemoveFromPanelActionPayload,
): WorkspaceActionResult {
  const panel = selectPanelById(state, payload.panelId);
  if (!panel) {
    return panelNotFound(payload.panelId);
  }

  if (payload.messagePathIds.length === 0) {
    return failure("At least one message path id is required.");
  }

  const messagePathIds = [...new Set(payload.messagePathIds)];
  const invalid: InvalidPath[] = [];
  const removals: (() => void)[] = [];

  const notInPanel = (messagePathId: string) => {
    invalid.push({
      messagePathId,
      reason: `Message path is not in panel "${payload.panelId}".`,
    });
  };

  if (isPlotPanelState(panel)) {
    messagePathIds.forEach((messagePathId) => {
      // The plot reducer appends series without deduplicating, so one
      // message path may back several series; remove every match.
      const matches = panel.data.filter(
        (series) => series.data.messagePath.id === messagePathId,
      );
      if (matches.length === 0) {
        notInPanel(messagePathId);
        return;
      }
      matches.forEach((series) => {
        removals.push(() =>
          dispatch(actions.removeSeriesFromPlotPanel(panel.id, series.id)),
        );
      });
    });
  } else if (isLogPanelState(panel)) {
    messagePathIds.forEach((messagePathId) => {
      if (!panel.data.some((data) => data.messagePath.id === messagePathId)) {
        notInPanel(messagePathId);
        return;
      }
      removals.push(() =>
        dispatch(
          actions.removeMessagePathFromLogPanel(panel.id, messagePathId),
        ),
      );
    });
  } else if (panel.type === PanelType.RawMessage) {
    messagePathIds.forEach((messagePathId) => {
      if (!panel.data.some((data) => data.messagePath.id === messagePathId)) {
        notInPanel(messagePathId);
        return;
      }
      removals.push(() =>
        dispatch(
          actions.removeMessagePathFromRawMessagePanel(panel.id, messagePathId),
        ),
      );
    });
  } else if (isImagePanelState(panel)) {
    messagePathIds.forEach((messagePathId) => {
      const clip = panel.data.find(
        (candidate) => candidate.data.messagePath.id === messagePathId,
      );
      if (!clip) {
        notInPanel(messagePathId);
        return;
      }
      removals.push(() =>
        dispatch(actions.removeClipFromImagePanel(panel.id, clip.id)),
      );
    });
  } else if (isMapPanelState(panel)) {
    // A map path bundles a lat/lon pair; removing either member removes the
    // whole path. Dedupe so a request naming both members removes it once.
    const pathIdsToRemove = new Set<string>();
    messagePathIds.forEach((messagePathId) => {
      const paths = panel.data.filter((path) =>
        path.data.some((geoItem) => geoItem.messagePath.id === messagePathId),
      );
      if (paths.length === 0) {
        notInPanel(messagePathId);
        return;
      }
      paths.forEach((path) => pathIdsToRemove.add(path.id));
    });
    pathIdsToRemove.forEach((pathId) => {
      removals.push(() =>
        dispatch(actions.removePathFromMapPanel(panel.id, pathId)),
      );
    });
  } else {
    return failure(
      `Removing message paths from a ${panel.type} panel is not supported.`,
    );
  }

  if (invalid.length > 0) {
    return failure(
      `Some message paths cannot be removed from panel "${payload.panelId}".`,
      invalid,
    );
  }

  removals.forEach((removal) => removal());
  return { ok: true, panelId: payload.panelId };
}

export function handleRemovePanel(
  state: State,
  dispatch: Dispatch,
  payload: RemovePanelActionPayload,
): WorkspaceActionResult {
  const panel = selectPanelById(state, payload.panelId);
  if (!panel) {
    return panelNotFound(payload.panelId);
  }

  dispatch(actions.removePanel(payload.panelId));
  return { ok: true, panelId: payload.panelId };
}

/**
 * Returns the reason a filter stage is invalid for a column, or undefined
 * when it is valid. Mirrors FilterBar's builder-row gates, plus two rules the
 * engine otherwise fails silently on: `between` without `value2` (a missing
 * bound normalizes to NaN and matches zero rows), and a log-time value given
 * in the wrong time unit.
 *
 * The log-time checks run only when `isLogTime` says the target is that
 * column; `earliestTime` is the earliest timestamp the panel's data reports,
 * or null when it reports none.
 */
function logFilterStageProblem(
  stage: FilterStage,
  allowedConditions: FilterCondition[],
  columnName: string,
  isLogTime: boolean,
  earliestTime: bigint | null,
): string | undefined {
  if (!allowedConditions.includes(stage.condition)) {
    return (
      `Condition "${stage.condition}" is not available on column "${columnName}". ` +
      `Available conditions: ${allowedConditions.join(", ")}.`
    );
  }
  // Checked ahead of the valueless early return below, which would otherwise
  // make it unreachable for is_empty/is_not_empty — and a stray value2 there
  // is not inert: columnFiltersEqual compares value2, so it defeats the
  // no-op dispatch guard.
  if (!needsSecondValue(stage.condition) && stage.value2 !== undefined) {
    return `Condition "${stage.condition}" does not take value2.`;
  }
  if (isValuelessCondition(stage.condition)) {
    return stage.value === ""
      ? undefined
      : `Condition "${stage.condition}" takes no value.`;
  }
  if (stage.value.trim() === "") {
    return `Condition "${stage.condition}" requires a non-empty value.`;
  }
  if (stage.condition === "matches_regex" && !isValidRegex(stage.value)) {
    return `"${stage.value}" is not a valid regular expression.`;
  }
  if (conditionMeta[stage.condition].numericOnly) {
    if (!isFiniteNumericString(stage.value)) {
      return `Condition "${stage.condition}" requires a numeric value; got "${stage.value}".`;
    }
    if (needsSecondValue(stage.condition)) {
      if (stage.value2 === undefined || stage.value2.trim() === "") {
        return 'Condition "between" requires value2 (the upper bound).';
      }
      if (!isFiniteNumericString(stage.value2)) {
        return `Condition "between" requires a numeric value2; got "${stage.value2}".`;
      }
    }
  }
  if (isLogTime) {
    return (
      logTimeValueProblem(stage.value, earliestTime) ??
      (stage.value2 !== undefined
        ? logTimeValueProblem(stage.value2, earliestTime)
        : undefined)
    );
  }
  return undefined;
}

/**
 * The earliest topic start time on the panel. Null when no topic carries a
 * start time that parses as a BigInt and is positive; missing, unparseable,
 * and non-positive start times are skipped. Used to tell a nanosecond-epoch
 * log-time value from one handed over in a coarser unit.
 */
function earliestTopicStartTime(data: TopicData[]): bigint | null {
  let earliest: bigint | null = null;
  for (const topicData of data) {
    const startTime = topicData.topic.startTime;
    if (startTime === undefined || startTime === null) {
      continue;
    }
    let parsed: bigint;
    try {
      parsed = BigInt(startTime);
    } catch {
      continue;
    }
    if (parsed > 0n && (earliest === null || parsed < earliest)) {
      earliest = parsed;
    }
  }
  return earliest;
}

export function handleSetLogPanelFilters(
  state: State,
  dispatch: Dispatch,
  payload: SetLogPanelFiltersActionPayload,
): WorkspaceActionResult {
  const panel = selectPanelById(state, payload.panelId);
  if (!panel) {
    return panelNotFound(payload.panelId);
  }
  if (!isLogPanelState(panel)) {
    return failure(
      `Panel "${payload.panelId}" is a ${panel.type} panel; column filters only apply to Log panels.`,
    );
  }
  if (payload.edits.length === 0) {
    return failure("At least one filter edit is required.");
  }

  // The live filter state, derived exactly as LogPanel derives it — so
  // validation sees the same columns and filters the panel does, whether or
  // not the panel is currently mounted.
  const current = deriveLiveColumnFilters(
    panel.config?.columnFilters,
    panel.data,
    getDynamicColumns(panel.data),
  );

  const invalid: InvalidTarget[] = [];
  const nextFilters: Record<string, ColumnFilter> = { ...current.filters };
  const earliestTime = earliestTopicStartTime(panel.data);
  // Which target already claimed each column this batch. getDynamicColumns
  // collapses distinct message paths that share a column, so two edits can
  // resolve to one key and silently compose in array order.
  const claimedBy = new Map<string, string>();

  for (const edit of payload.edits) {
    // Resolve the target to a column. The AI addresses real columns by
    // message path id; the columnId encoding is an internal detail.
    let columnId: string;
    let targetLabel: string;
    if (edit.target === "all_columns") {
      columnId = ALL_COLUMNS_ID;
      targetLabel = "all_columns";
    } else if (edit.target === "log_time") {
      columnId = LOG_TIME_ID;
      targetLabel = "log_time";
    } else {
      const { messagePathId } = edit.target;
      targetLabel = messagePathId;
      const topicData = panel.data.find(
        (data) => data.messagePath.id === messagePathId,
      );
      if (topicData === undefined) {
        invalid.push({
          target: messagePathId,
          reason: `Message path is not on panel "${payload.panelId}".`,
        });
        continue;
      }
      columnId = columnIdForParts(topicData.messagePath.parts);
    }

    const claimant = claimedBy.get(columnId);
    if (claimant !== undefined) {
      invalid.push({
        target: targetLabel,
        reason:
          claimant === targetLabel
            ? "Named twice in one call; give a target at most one edit."
            : `Resolves to the same column as "${claimant}", which this call already edits. ` +
              "Combine them into one edit.",
      });
      continue;
    }
    claimedBy.set(columnId, targetLabel);

    const column = current.columns[columnId];
    if (column === undefined) {
      // Unreachable: every resolved target has a column. Kept to narrow the type.
      invalid.push({
        target: targetLabel,
        reason: `Column was not found on panel "${payload.panelId}".`,
      });
      continue;
    }

    if (edit.mode === "append" && edit.stages.length === 0) {
      invalid.push({
        target: targetLabel,
        reason:
          "Append requires at least one stage. To clear a column's filter, use replace with no stages.",
      });
      continue;
    }

    const allowedConditions = getConditionsForDataType(column.dataType);
    const stageProblem = edit.stages
      .map((stage) =>
        logFilterStageProblem(
          stage,
          allowedConditions,
          column.name,
          columnId === LOG_TIME_ID,
          earliestTime,
        ),
      )
      .find((reason) => reason !== undefined);
    if (stageProblem !== undefined) {
      invalid.push({ target: targetLabel, reason: stageProblem });
      continue;
    }

    // Persist trimmed, as the filter bar's builder row does. Storing raw would
    // let `contains " error "` through with padding the user cannot see.
    const stages = edit.stages.map((stage) => ({
      ...stage,
      value: stage.value.trim(),
      ...(stage.value2 !== undefined && { value2: stage.value2.trim() }),
    }));

    const existing = nextFilters[columnId];
    const combinator = edit.combinator ?? existing?.combinator ?? "or";
    if (edit.mode === "replace") {
      if (stages.length === 0) {
        delete nextFilters[columnId];
      } else {
        nextFilters[columnId] = { stages, combinator };
      }
    } else {
      nextFilters[columnId] = {
        stages: [...(existing?.stages ?? []), ...stages],
        combinator,
      };
    }
  }

  if (invalid.length > 0) {
    return targetFailure(
      `Some filter edits cannot be applied to panel "${payload.panelId}".`,
      invalid,
    );
  }

  // The panel's mutual-exclusivity rule, checked on the final map so one
  // batch may clear per-column filters and set an all-columns search. The
  // model cannot read filters back by message path id, so the failure names
  // each conflicting column the way the model addresses it.
  if (hasAllColumnsFilter(nextFilters) && hasPerColumnFilters(nextFilters)) {
    const conflicts = Object.keys(nextFilters)
      .filter((key) => key !== ALL_COLUMNS_ID && key !== LOG_TIME_ID)
      .map((columnId) => ({
        target:
          panel.data.find(
            (data) => columnIdForParts(data.messagePath.parts) === columnId,
          )?.messagePath.id ?? columnId,
        reason:
          "Has a per-column filter, which an all-columns search cannot coexist with. " +
          "Clear it in the same call (mode replace, stages: []) or drop the all_columns edit.",
      }));
    return targetFailure(
      "An all-columns search and per-column filters are mutually exclusive on a Log panel " +
        "(log_time filters are exempt).",
      conflicts,
    );
  }

  // searchVisible doubles as the "filtering enabled" switch — without it the
  // filter is persisted but invisible — so a call that leaves filters opens
  // the bar. A call that leaves none keeps the bar as it was: opening it would
  // start an index build on a small panel for a request to remove filtering.
  //
  // "As it was" must be written down. A config saved before searchVisible
  // existed has no stored value, and its bar is open only because it carries
  // filters (or a legacy searchFilter). This write drops both, so pin what
  // resolveSearchVisible says now, as the panel's own commit path does.
  //
  // Skip the dispatch when neither the filters nor the bar would change;
  // pinning an absent field to the value it already resolves to is not a
  // change.
  const hasFilters = Object.keys(nextFilters).length > 0;
  const searchVisible = hasFilters || resolveSearchVisible(panel.config);
  if (
    !columnFiltersEqual(current.filters, nextFilters) ||
    searchVisible !== resolveSearchVisible(panel.config)
  ) {
    dispatch(
      actions.setLogPanelConfig(panel.id, {
        columnFilters: nextFilters,
        searchFilter: undefined,
        searchVisible,
      }),
    );
  }

  // Above the auto-build threshold the index build is user-confirmed, and
  // panel state cannot see whether it already ran — report conservatively so
  // the model can tell the user to click "Build Index" if rows don't filter.
  const applied =
    estimateTopicMessageCount(panel.data) > A_LOT_OF_LOGS
      ? "pending_index_build"
      : "applied";
  return {
    ok: true,
    panelId: panel.id,
    panelType: PanelType.Log,
    details: { applied },
  };
}
